import { Controller } from "@hotwired/stimulus";

// Gestion des favoris du simulateur (issue #296).
//
// Trois emplacements partagent le même état (l'ensemble des `favorite listing
// ids` du profil persisté) :
//  - les étoiles des cards du catalogue (data-favorites-target="star")
//  - l'étoile du bloc « Votre bien immobilier » (data-favorites-target="locationStar")
//  - les cards de l'onglet « Favoris »
//
// Toute action (ajout/suppression) met à jour l'état local, persiste côté
// serveur (routes add_favorite / delete_favorite du profil) et resynchronise
// les étoiles des trois emplacements sans rechargement de page.
export default class extends Controller {
  static targets = ["star", "locationStar", "panel", "list", "count", "profileName", "emptyMessage", "loadingMessage"];
  static values = {
    enabled: { type: Boolean, default: false },
    profileId: String,
    ids: { type: Array, default: [] },
    addUrl: String,
    deleteUrl: String,
    searchUrl: String,
  };

  connect() {
    if (!this.enabledValue) return;
    this.favoriteIds = new Set((this.idsValue || []).map((id) => String(id)));

    // La liste des favoris est « périmée » tant qu'elle n'a pas été chargée, et
    // le redevient dès qu'un critère (profil / filtres) change. Évite de relancer
    // une requête `search` à chaque ouverture de l'onglet sans changement.
    this.favoritesStale = true;

    // Resynchronise les étoiles après chaque rendu (recherche catalogue ou
    // détails du bien actif), pour que l'état local reste la source de vérité.
    this.onRemoteSuccess = (e) => this.handleRemoteSuccess(e);
    this.element.addEventListener("remote:update:success", this.onRemoteSuccess);

    // Toute modification d'un critère (profil client / filtres) périme la liste
    // des favoris et, si l'onglet est affiché, déclenche un rechargement debounced.
    this.onCriteriaChange = (e) => this.handleCriteriaChange(e);
    this.criteriaForms = [
      this.element.querySelector("#edit-profile-form"),
      this.element.querySelector("#listing-filter-form"),
    ].filter(Boolean);
    this.criteriaForms.forEach((form) => {
      ["input", "change"].forEach((ev) => form.addEventListener(ev, this.onCriteriaChange, true));
    });

    this.syncStars();
    this.updateLocationStar();
  }

  disconnect() {
    try { this.element.removeEventListener("remote:update:success", this.onRemoteSuccess); } catch (_) {}
    if (this.criteriaForms) {
      this.criteriaForms.forEach((form) => {
        ["input", "change"].forEach((ev) => form.removeEventListener(ev, this.onCriteriaChange, true));
      });
    }
    if (this._reloadTimer) clearTimeout(this._reloadTimer);
  }

  handleCriteriaChange(e) {
    // Ignore les évènements synthétiques émis lors de l'application des
    // suggestions api-sync (sinon boucle de rechargement).
    if (e && (e.__investDetailsFromApply || (e.detail && e.detail.fromInvestApply))) return;
    const name = e?.target?.getAttribute?.("name") || "";
    if (name === "page" || name === "per" || name === "profile_id") return;

    this.favoritesStale = true;
    if (this.favoritesPanelVisible()) this.scheduleReload();
  }

  scheduleReload() {
    if (this._reloadTimer) clearTimeout(this._reloadTimer);
    this._reloadTimer = setTimeout(() => this.loadFavorites(), 300);
  }

  favoritesPanelVisible() {
    return this.hasPanelTarget && !this.panelTarget.classList.contains("d-none");
  }

  handleRemoteSuccess(e) {
    const channel = e?.detail?.channel;
    // Cards (catalogue ou favoris) re-rendues : on réaccorde leurs étoiles.
    if (channel === "listings_search" || channel === "favorites_list") {
      setTimeout(() => this.syncStars(), 0);
    }
    // Le bien actif a changé : on met à jour l'étoile du bloc « Votre bien ».
    if (channel === "listing_details") {
      setTimeout(() => this.updateLocationStar(), 0);
    }
  }

  // ---------- Actions ----------
  toggle(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    try { event?.stopPropagation?.(); } catch (_) {}
    const el = event?.currentTarget;
    const id = el && (el.dataset.favoriteListingId || el.getAttribute("data-favorite-listing-id"));
    this.toggleId(id);
  }

  toggleActive(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    try { event?.stopPropagation?.(); } catch (_) {}
    this.toggleId(this.activeListingId());
  }

  // Au clic sur l'onglet Favoris : ne (re)charge la liste que si elle est périmée
  // (premier affichage ou critère modifié depuis le dernier chargement).
  show(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    if (this.favoritesStale) this.loadFavorites();
  }

  // Resynchronise le contrôleur sur le profil nouvellement actif (#305), appelé
  // par `profiles_controller.js#applyResponse` lors d'un changement de profil
  // sans rechargement de page. Met à jour l'état local (ids), les URLs de
  // persistance, le `profile_id` caché du formulaire de filtres, le header de
  // l'onglet (compteur + nom) et marque la liste comme périmée pour qu'elle se
  // recharge au prochain affichage (ou immédiatement si l'onglet est ouvert).
  applyProfile({ profileId, ids, addUrl, deleteUrl, count, label } = {}) {
    if (!this.enabledValue) return;

    if (profileId != null) this.profileIdValue = String(profileId);
    if (addUrl) this.addUrlValue = String(addUrl);
    if (deleteUrl) this.deleteUrlValue = String(deleteUrl);
    this.favoriteIds = new Set((ids || []).map((id) => String(id)));

    // Aligne le `profile_id` caché du formulaire de filtres pour que la
    // recherche favoris cible le bon profil.
    const filterForm = this.element.querySelector("#listing-filter-form");
    const profileIdField = filterForm && filterForm.querySelector('input[name="profile_id"]');
    if (profileIdField && profileId != null) profileIdField.value = String(profileId);

    // Header de l'onglet Favoris (rendu serveur statiquement au chargement).
    if (this.hasCountTarget) {
      this.countTarget.textContent = String(count != null ? count : this.favoriteIds.size);
    }
    if (this.hasProfileNameTarget && label != null) {
      this.profileNameTarget.textContent = label;
    }

    this.favoritesStale = true;
    this.syncStars();
    this.updateLocationStar();
    this.toggleEmptyMessage();
    if (this.favoritesPanelVisible()) this.scheduleReload();
  }

  // ---------- Toggle logic ----------
  toggleId(listingId) {
    if (!this.enabledValue) return;
    const id = listingId == null ? "" : String(listingId);
    if (!id) return;

    const wasFavorite = this.favoriteIds.has(id);
    if (wasFavorite) {
      this.favoriteIds.delete(id);
    } else {
      this.favoriteIds.add(id);
    }

    // La composition des favoris a changé : la liste devra être rechargée au
    // prochain affichage de l'onglet (l'ajout/suppression visible est géré
    // immédiatement ci-dessous pour la card concernée).
    this.favoritesStale = true;

    this.syncStars();
    this.updateLocationStar();
    this.updateCount();
    if (wasFavorite) this.removeFavoriteCard(id);
    this.emitChanged();

    this.persist(id, !wasFavorite).catch(() => {
      // Rollback en cas d'échec réseau.
      if (wasFavorite) {
        this.favoriteIds.add(id);
      } else {
        this.favoriteIds.delete(id);
      }
      this.syncStars();
      this.updateLocationStar();
      this.updateCount();
      this.emitChanged();
    });
  }

  persist(listingId, add) {
    const url = add ? this.addUrlValue : this.deleteUrlValue;
    if (!url) return Promise.reject(new Error("missing url"));
    const headers = { "Content-Type": "application/json", Accept: "application/json" };
    const csrf = this.csrfToken();
    if (csrf) headers["X-CSRF-Token"] = csrf;
    return fetch(url, {
      method: "POST",
      headers,
      credentials: "same-origin",
      body: JSON.stringify({ listing_id: listingId }),
    }).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r;
    });
  }

  emitChanged() {
    try {
      this.element.dispatchEvent(new CustomEvent("favorites:changed", {
        bubbles: true,
        detail: { ids: Array.from(this.favoriteIds) },
      }));
    } catch (_) {}
  }

  // ---------- Star synchronization ----------
  syncStars() {
    // Requête DOM directe (plutôt que starTargets) pour éviter une course avec
    // l'enregistrement des targets Stimulus après un rendu dynamique des cards.
    const stars = this.element.querySelectorAll("[data-favorites-target~='star']");
    stars.forEach((star) => {
      const id = star.dataset.favoriteListingId || star.getAttribute("data-favorite-listing-id");
      if (!id) return;
      const isFav = this.favoriteIds.has(String(id));
      star.setAttribute("data-favorite", isFav ? "true" : "false");
      try { star.classList.toggle("is-favorite", isFav); } catch (_) {}
    });
  }

  updateLocationStar() {
    if (!this.hasLocationStarTarget) return;
    const star = this.locationStarTarget;
    const id = this.activeListingId();
    if (!id) {
      star.hidden = true;
      return;
    }
    star.hidden = false;
    const isFav = this.favoriteIds.has(String(id));
    star.setAttribute("data-favorite", isFav ? "true" : "false");
    try { star.classList.toggle("is-favorite", isFav); } catch (_) {}
  }

  updateCount() {
    if (this.hasCountTarget) this.countTarget.textContent = String(this.favoriteIds.size);
    this.toggleEmptyMessage();
  }

  toggleEmptyMessage() {
    if (!this.hasListTarget) return;
    const hasCards = !!this.listTarget.querySelector("[data-favorite-listing-id]");
    if (this.hasEmptyMessageTarget) this.emptyMessageTarget.hidden = hasCards;
  }

  removeFavoriteCard(listingId) {
    if (!this.hasListTarget) return;
    const star = this.listTarget.querySelector(
      `[data-favorite-listing-id="${this.cssEscape(String(listingId))}"]`
    );
    const card = star?.closest(".simulator-card");
    if (card) {
      card.remove();
      this.toggleEmptyMessage();
    }
  }

  // ---------- Favorites list fetch ----------
  loadFavorites() {
    if (!this.enabledValue || !this.searchUrlValue) return;
    if (this._reloadTimer) { clearTimeout(this._reloadTimer); this._reloadTimer = null; }
    this.favoritesStale = false;
    this.showLoading(true);

    const payload = this.buildSearchPayload();
    payload.only_favorites = true;
    payload.profile_id = this.profileIdValue;
    payload.per = 50;
    payload.page = 1;

    const headers = { "Content-Type": "application/json", Accept: "application/json" };
    const apiToken = document.querySelector('meta[name="api-token"]')?.getAttribute("content");
    if (apiToken) headers["Authorization"] = `Bearer ${apiToken}`;

    fetch(this.searchUrlValue, {
      method: "POST",
      headers,
      credentials: "same-origin",
      body: JSON.stringify(payload),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((json) => {
        this.renderFavorites(json);
      })
      .catch(() => {
        this.renderFavorites({ items: [] });
      })
      .finally(() => {
        this.showLoading(false);
      });
  }

  renderFavorites(json) {
    if (!this.hasPanelTarget) return;
    // Réutilise le rendu remote-bind (remote-repeat) du panneau favoris.
    try {
      this.panelTarget.dispatchEvent(new CustomEvent("remote:update:success", {
        bubbles: false,
        detail: { channel: "favorites_list", json: json || { items: [] } },
      }));
    } catch (_) {}
    setTimeout(() => {
      this.syncStars();
      this.toggleEmptyMessage();
      // Révèle la liste : le gabarit serveur (card « vide ») était masqué via
      // `d-none` au rendu initial pour éviter de l'afficher tant que les
      // favoris n'avaient pas été chargés. Une fois les items injectés par
      // remote-bind, on retire `d-none` (idempotent, jamais re-posé).
      if (this.hasListTarget) this.listTarget.classList.remove("d-none");
      this.emitRendered();
    }, 0);
  }

  // Signale aux autres contrôleurs (listing_results) que les cards favoris
  // viennent d'être (re)rendues, pour qu'ils réappliquent leur état dépendant
  // du DOM (ex. surbrillance `.active` du bien sélectionné, #353).
  emitRendered() {
    try {
      this.element.dispatchEvent(new CustomEvent("favorites:rendered", { bubbles: true }));
    } catch (_) {}
  }

  showLoading(show) {
    if (this.hasLoadingMessageTarget) this.loadingMessageTarget.classList.toggle("d-none", !show);
    if (show && this.hasEmptyMessageTarget) this.emptyMessageTarget.hidden = true;
  }

  // Construit le payload de recherche à partir des formulaires Profil client et
  // Filtres (mêmes critères que le catalogue), pour des valeurs cohérentes.
  buildSearchPayload() {
    const payload = {};
    const forms = [
      this.element.querySelector("#edit-profile-form"),
      this.element.querySelector("#listing-filter-form"),
    ].filter(Boolean);

    forms.forEach((form) => {
      for (const [key, value] of new FormData(form).entries()) {
        if (key === "page" || key === "per" || key === "profile_id") continue;
        this.assignNested(payload, key, value);
      }
    });

    return payload;
  }

  assignNested(target, key, value) {
    if (value === null || value === undefined || value === "") return;
    const isArray = /\[\]$/.test(key);
    const cleanKey = isArray ? key.replace(/\[\]$/, "") : key;
    const path = cleanKey.replace(/]/g, "").split("[").filter(Boolean);
    let obj = target;
    for (let i = 0; i < path.length; i++) {
      const part = path[i];
      if (i === path.length - 1) {
        if (isArray) {
          if (!Array.isArray(obj[part])) obj[part] = [];
          obj[part].push(value);
        } else {
          obj[part] = value;
        }
      } else {
        if (!obj[part] || typeof obj[part] !== "object") obj[part] = {};
        obj = obj[part];
      }
    }
  }

  // ---------- Helpers ----------
  activeListingId() {
    const field = this.element.querySelector("[data-listing-results-target='listingId']");
    const value = field && field.value;
    return value ? String(value) : "";
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") : null;
  }

  cssEscape(str) {
    try {
      if (typeof CSS !== "undefined" && CSS.escape) return CSS.escape(str);
    } catch (_) {}
    return String(str).replace(/["\\]/g, "\\$&");
  }
}
